import { useEffect, useRef } from 'react';
import L from 'leaflet';

import { Position, ZoneGeometry } from './MapEditor';

export type PolygonStyle = {
  color?: string;
  fillColor?: string;
  fillOpacity?: number;
  weight?: number;
};

export interface MarkerData {
  lat: number;
  lng: number;
  label: string;
}

export interface MapViewerProps {
  geojson?: ZoneGeometry | null;
  markers?: MarkerData[];
  center?: Position;
  zoom?: number;
  height?: string;
  className?: string;
  style?: PolygonStyle;
  disabled?: boolean;
  loading?: boolean;
  draggableMarkers?: boolean;
  onMarkerDragEnd?: (coords: { lat: number; lng: number }, marker: MarkerData) => void;
}

/** Acepta Polygon y MultiPolygon: una zona puede tener varios polígonos. */
const isValidPolygon = (geojson?: ZoneGeometry | null) =>
  Boolean(
    geojson &&
      (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') &&
      Array.isArray(geojson.coordinates) &&
      geojson.coordinates.length > 0 &&
      Array.isArray(geojson.coordinates[0]) &&
      geojson.coordinates[0].length > 0
  );

const getMarkersSignature = (markers?: MarkerData[]) =>
  markers?.map((marker) => `${marker.lat}:${marker.lng}:${marker.label}`).join('|') ?? '';

const draggableMarkerIcon = L.divIcon({
  className: '',
  html: "<div style='width:28px;height:28px;border-radius:50%;background:#1f78b4;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.35);cursor:grab;'></div>",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const markerIcon = L.divIcon({
  className: '',
  html: "<div style='width:30px;height:42px;position:relative;'><div style='width:30px;height:30px;border-radius:50% 50% 50% 0;background:#ff7500;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.35);transform:rotate(-45deg);position:absolute;left:0;top:0;'></div><div style='width:10px;height:10px;border-radius:50%;background:white;position:absolute;left:10px;top:10px;'></div></div>",
  iconSize: [30, 42],
  iconAnchor: [15, 30],
});

const MapViewer = ({
  geojson,
  markers,
  center = [-34.9011, -56.1645],
  zoom = 20,
  height = 'h-96',
  className = '',
  style = {
    color: '#3388ff',
    fillColor: '#3388ff',
    fillOpacity: 0.3,
    weight: 2,
  },
  disabled = false,
  loading = false,
  draggableMarkers = false,
  onMarkerDragEnd,
}: MapViewerProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const polygonLayerRef = useRef<L.GeoJSON | null>(null);
  const onMarkerDragEndRef = useRef(onMarkerDragEnd);

  const centerLat = center[0];
  const centerLng = center[1];
  const markersSignature = getMarkersSignature(markers);

  useEffect(() => {
    onMarkerDragEndRef.current = onMarkerDragEnd;
  }, [onMarkerDragEnd]);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current || disabled || loading) {
      return;
    }

    try {
      mapInstance.current = L.map(mapRef.current, {
        center: [centerLat, centerLng],
        zoom,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
      }).addTo(mapInstance.current);

      mapInstance.current.whenReady(() => {
        setTimeout(() => mapInstance.current?.invalidateSize(), 0);
      });

      const resizeObserver = new ResizeObserver(() => {
        mapInstance.current?.invalidateSize();
      });
      resizeObserver.observe(mapRef.current);

      return () => {
        resizeObserver.disconnect();
        if (mapInstance.current) {
          mapInstance.current.remove();
          mapInstance.current = null;
        }
        markersLayerRef.current = null;
        polygonLayerRef.current = null;
      };
    } catch (error) {
      console.error('Error inicializando mapa:', error);
    }
  }, [disabled, loading]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map || disabled || loading) {
      return;
    }

    if (polygonLayerRef.current) {
      polygonLayerRef.current.remove();
      polygonLayerRef.current = null;
    }

    if (!isValidPolygon(geojson)) {
      return;
    }

    const polygon = L.geoJSON(geojson!, { style }).addTo(map);
    polygonLayerRef.current = polygon;

    const bounds = polygon.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [16, 16] });
    }
  }, [geojson, style, disabled, loading]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map || disabled || loading) {
      return;
    }

    if (markersLayerRef.current) {
      markersLayerRef.current.clearLayers();
      map.removeLayer(markersLayerRef.current);
      markersLayerRef.current = null;
    }

    if (!markers || markers.length === 0) {
      return;
    }

    const group = L.layerGroup();
    const bounds: L.LatLngBoundsExpression = [];
    let clickHandler: ((event: L.LeafletMouseEvent) => void) | null = null;

    markers.forEach((marker) => {
      const lat = Number(marker.lat);
      const lng = Number(marker.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }
      const markerOptions: L.MarkerOptions = {
        icon: draggableMarkers ? draggableMarkerIcon : markerIcon,
        draggable: draggableMarkers,
        autoPan: draggableMarkers,
        zIndexOffset: 1000,
      };
      const leafletMarker = L.marker([lat, lng], markerOptions);
      if (draggableMarkers && onMarkerDragEnd) {
        leafletMarker.on('add', () => {
          const element = leafletMarker.getElement();
          if (element) {
            element.style.cursor = 'grab';
            element.style.pointerEvents = 'auto';
          }
          leafletMarker.dragging?.enable();
        });
        leafletMarker.on('dragstart', () => {
          const element = leafletMarker.getElement();
          if (element) {
            element.style.cursor = 'grabbing';
          }
        });
        leafletMarker.on('dragend', () => {
          const nextPosition = leafletMarker.getLatLng();
          const element = leafletMarker.getElement();
          if (element) {
            element.style.cursor = 'grab';
          }
          onMarkerDragEndRef.current?.(
            { lat: nextPosition.lat, lng: nextPosition.lng },
            marker
          );
        });
      }
      if (!draggableMarkers) {
        leafletMarker.bindPopup(marker.label);
      }
      leafletMarker.addTo(group);
      bounds.push([lat, lng]);
    });

    group.addTo(map);
    markersLayerRef.current = group;
    if (draggableMarkers && onMarkerDragEnd && markers[0]) {
      map.getContainer().style.cursor = 'crosshair';
      clickHandler = (event: L.LeafletMouseEvent) => {
        onMarkerDragEndRef.current?.(
          { lat: event.latlng.lat, lng: event.latlng.lng },
          markers[0]
        );
      };
      map.on('click', clickHandler);
    }

    const cleanupMapInteraction = () => {
      if (clickHandler) {
        map.off('click', clickHandler);
      }
      map.getContainer().style.cursor = '';
    };

    if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [50, 50] });
      return cleanupMapInteraction;
    } else if (bounds.length === 1) {
      const pin = bounds[0] as Position;
      map.setView(pin, zoom);
      const timeoutId = window.setTimeout(() => {
        const activeMap = mapInstance.current;
        if (!activeMap?.getContainer()?.isConnected) {
          return;
        }
        try {
          activeMap.invalidateSize();
          activeMap.setView(pin, zoom);
        } catch {
          // El mapa pudo desmontarse antes de que corra el timeout.
        }
      }, 0);

      return () => {
        window.clearTimeout(timeoutId);
        cleanupMapInteraction();
      };
    }
    return cleanupMapInteraction;
  }, [
    markersSignature,
    zoom,
    disabled,
    loading,
    draggableMarkers,
  ]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map || disabled || loading) {
      return;
    }

    map.setView([centerLat, centerLng], zoom);
  }, [centerLat, centerLng, zoom, disabled, loading]);

  return (
    <div ref={mapRef} className={`${height} w-full min-w-0 ${className}`} />
  );
};

export default MapViewer;
